import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../utils/api";
import { useGlobalState } from '../contexts/GlobalContext';
import LoadingSpinner from "../Components/LoadingSpinner";
import { Form, Button, Alert, Card, Stack } from 'react-bootstrap';
import { isRealtor } from '../utils/userAccess';

function BrokerageAgents() {
  const [agents, setAgents] = useState([]);
  const [joinRequests, setJoinRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [memberIdentifier, setMemberIdentifier] = useState('');
  const [memberError, setMemberError] = useState('');
  const [memberSuccess, setMemberSuccess] = useState('');
  const [addingMember, setAddingMember] = useState(false);
  const [requestsError, setRequestsError] = useState('');
  const [requestActionId, setRequestActionId] = useState(null);
  const { state } = useGlobalState();
  const navigate = useNavigate();
  const team = state.user?.team;
  const teamId = team?.id || null;
  const teamName = team?.name || '';
  const teamMemberRole = team?.memberRole || team?.member_role || state.user?.team_member_role || null;
  const canManageRequests = ['owner', 'admin'].includes(teamMemberRole) || state.user?.role === 'Titan';
  const realtorView = isRealtor(state.user);
  const heading = realtorView ? `Agents at ${teamName || 'Team'}` : `${teamName || 'Team'} Members`;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await apiFetch("fetchTeamMembers", {
          queryParams: { team_id: teamId },
        });

        if (result.success) {
          setAgents(result.agents);
        } else {
          alert("Failed to fetch agents: " + result.message);
        }

        if (canManageRequests) {
          const requestsResult = await apiFetch('fetchTeamJoinRequests', {
            queryParams: { team_id: teamId, status: 'pending' },
          });
          if (requestsResult.success) {
            setJoinRequests(requestsResult.requests || []);
          }
        } else {
          setJoinRequests([]);
        }
      } catch (err) {
        console.error(err);
        alert("Something went wrong.");
      } finally {
        setLoading(false);
      }
    };

    if (!teamId) {
      setLoading(false);
      return;
    }

    fetchData();
  }, [teamId, canManageRequests]);

  const refreshTeamData = async () => {
    const refreshed = await apiFetch("fetchTeamMembers", {
      queryParams: { team_id: teamId },
    });
    if (refreshed.success) {
      setAgents(refreshed.agents || []);
    }

    if (canManageRequests) {
      const requestsResult = await apiFetch('fetchTeamJoinRequests', {
        queryParams: { team_id: teamId, status: 'pending' },
      });
      if (requestsResult.success) {
        setJoinRequests(requestsResult.requests || []);
      }
    }
  };

  if (!teamId) {
    return (
      <div className="container">
        <h3 className="mt-3">{realtorView ? 'Broker Panel' : 'Team'}</h3>
        <p className="text-muted">
          {realtorView
            ? 'Join or update a brokerage in Account before viewing this panel.'
            : 'Create or join a business in Account before managing your team.'}
        </p>
      </div>
    );
  }

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!memberIdentifier.trim()) return;

    setAddingMember(true);
    setMemberError('');
    setMemberSuccess('');
    try {
      const result = await apiFetch('addTeamMember', {
        method: 'POST',
        body: JSON.stringify({ identifier: memberIdentifier.trim() }),
      });
      setMemberSuccess(result.message || 'Team member added.');
      setMemberIdentifier('');
      await refreshTeamData();
    } catch (err) {
      setMemberError(err.message || 'Unable to add team member.');
    } finally {
      setAddingMember(false);
    }
  };

  const handleJoinRequestDecision = async (requestId, decision) => {
    setRequestsError('');
    setMemberSuccess('');
    setRequestActionId(requestId);
    try {
      const result = await apiFetch('respondTeamJoinRequest', {
        method: 'POST',
        body: JSON.stringify({
          request_id: requestId,
          decision,
        }),
      });
      setMemberSuccess(result.message || 'Join request updated.');
      await refreshTeamData();
    } catch (err) {
      setRequestsError(err.message || 'Unable to update join request.');
    } finally {
      setRequestActionId(null);
    }
  };

  const handleAgentClick = (publicUserId) => {
    if (!publicUserId) return;
    navigate(`/profile/${publicUserId}`);
  };

  if (loading) return <LoadingSpinner message="Loading agents…" />;
  const joinRequestsCard = canManageRequests ? (
    <Card className="shadow-sm mb-4">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <div>
            <div className="fw-semibold">Pending Join Requests</div>
            <div className="text-muted small">Approve or reject users asking to join this team.</div>
          </div>
        </div>

        {requestsError && <Alert variant="danger">{requestsError}</Alert>}

        {joinRequests.length === 0 ? (
          <div className="text-muted small">No pending join requests.</div>
        ) : (
          <Stack gap={3}>
            {joinRequests.map((request) => {
              const fullName = `${request.firstName || ''} ${request.lastName || ''}`.trim() || request.email || request.userId;
              return (
                <Card key={request.id} className="border">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                      <div>
                        <div className="fw-semibold">{fullName}</div>
                        <div className="small text-muted">
                          {request.email || 'No email'}
                          {request.userId ? ` • ${request.userId}` : ''}
                        </div>
                        <div className="small text-muted">
                          {[request.title, request.profession].filter(Boolean).join(' • ') || 'No title provided'}
                        </div>
                        {request.message && (
                          <div className="small mt-2">{request.message}</div>
                        )}
                      </div>
                      <Stack direction="horizontal" gap={2}>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          disabled={requestActionId === request.id}
                          onClick={() => handleJoinRequestDecision(request.id, 'rejected')}
                        >
                          Reject
                        </Button>
                        <Button
                          variant="primary"
                          size="sm"
                          disabled={requestActionId === request.id}
                          onClick={() => handleJoinRequestDecision(request.id, 'approved')}
                        >
                          {requestActionId === request.id ? 'Saving...' : 'Approve'}
                        </Button>
                      </Stack>
                    </div>
                  </Card.Body>
                </Card>
              );
            })}
          </Stack>
        )}
      </Card.Body>
    </Card>
  ) : null;

  return (
    <div className="container">
      <h3 className="mt-3">{heading}</h3>
      {joinRequestsCard}
      {!realtorView && (
        <form className="mb-3" onSubmit={handleAddMember}>
          <Form.Group className="mb-2">
            <Form.Label>Add employee by email or user ID</Form.Label>
            <Form.Control
              type="text"
              value={memberIdentifier}
              placeholder="employee@example.com or user123"
              onChange={(e) => setMemberIdentifier(e.target.value)}
            />
          </Form.Group>
          <Button type="submit" disabled={addingMember || !memberIdentifier.trim()}>
            {addingMember ? 'Adding...' : 'Add to Team'}
          </Button>
        </form>
      )}
      {memberError && <Alert variant="danger">{memberError}</Alert>}
      {memberSuccess && <Alert variant="success">{memberSuccess}</Alert>}
      {agents.length === 0 ? (
        <p>{realtorView ? 'No agents found for this brokerage.' : 'No team members found yet.'}</p>
      ) : (
        <div className="row">
        {agents.map((agent) => {
          const agentUserId = agent.user_id ?? agent.userId;
          return (
            <div
              className="col-md-4 mb-3"
              key={agentUserId || `${agent.email}-${agent.id}`}
            >
              <div
                className={`card shadow-sm${
                  agentUserId ? " cursor-pointer" : ""
                }`}
                role={agentUserId ? "button" : undefined}
                tabIndex={agentUserId ? 0 : undefined}
                onClick={() => handleAgentClick(agentUserId)}
                onKeyDown={(event) => {
                  if (!agentUserId) return;
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleAgentClick(agentUserId);
                  }
                }}
                style={agentUserId ? { cursor: "pointer" } : undefined}
              >
                <div className="card-body">
                  <h5 className="card-title">
                    {agent.firstName} {agent.lastName}
                  </h5>
                  <p className="card-text">
                    <strong>Email:</strong> {agent.email} <br />
                    <strong>Phone:</strong> {agent.cellPhone} <br />
                    <strong>Title:</strong> {agent.title} <br />
                    <strong>Role:</strong> {agent.memberRole || agent.role} <br />
                    <strong>Contacts:</strong> {agent.contactCount} <br />
                    <strong>Listings:</strong> {agent.listingCount} <br />
                    <strong>Tasks:</strong> {agent.taskCount}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
        </div>
      )}
    </div>
  );
}

export default BrokerageAgents;
